import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePatient } from '@ohif/ui';
import { generateTemporalToken } from '../utils/generateTemporalToken';
import Svg from '../../../ui/src/components/Svg';

const TOTAL_SECONDS = 5;

export default function ViewerRedirect() {
  const [searchParams] = useSearchParams();

  const { patient, project } = usePatient();

  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [loading, setLoading] = useState(true);
  const [urlFinal, setUrlFinal] = useState('');
  const [attemptedClose, setAttemptedClose] = useState(false);

  const viewerName = searchParams.get('viewer') || searchParams.get('browser') || 'Viewer';

  const rawDestination =
    searchParams.get('dest') ||
    searchParams.get('url') ||
    searchParams.get('destination') ||
    searchParams.get('redirect') ||
    '';

  const destination = decodeURIComponent(rawDestination);

  const typeParam = (
    searchParams.get('type') ||
    searchParams.get('target') ||
    'viewer'
  ).toLowerCase();

  const isBrowserRedirect = typeParam === 'browser' || Boolean(searchParams.get('browser'));

  const shouldAddTicket = !isBrowserRedirect;

  const patientId = patient;

  const projectId = project;

  const waitingForPatientContext = shouldAddTicket && (!patientId || !projectId);

  useEffect(() => {
    const prepareUrl = async () => {
      setLoading(true);

      if (!destination) {
        setLoading(false);
        return;
      }

      if (waitingForPatientContext) {
        return;
      }

      let url = destination;

      try {
        if (shouldAddTicket && patientId && projectId) {
          const ticket = await generateTemporalToken(patientId, projectId);

          if (ticket) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}ticket=${ticket}`;
          }
        }

        setUrlFinal(url);
      } catch (err) {
        console.error(err);
        setUrlFinal(url);
      } finally {
        setLoading(false);
      }
    };

    prepareUrl();
  }, [patientId, projectId, destination, shouldAddTicket, waitingForPatientContext]);

  useEffect(() => {
    if (loading || !urlFinal) return;

    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          window.location.href = urlFinal;
          return TOTAL_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [loading, urlFinal]);

  const handleClose = () => {
    setAttemptedClose(true);
    window.close();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: '#fafafa',
        fontFamily: 'system-ui',
      }}
    >
      {/* Header with logo */}
      <div
        style={{
          background: '#d9d9d9',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #ccc',
        }}
      >
        <Svg
          name="logo-vidaa-black"
          style={{ height: '32px', width: 'auto' }}
        />
      </div>

      {/* Content area */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <div
          style={{
            width: 450,
            textAlign: 'center',
            padding: 40,
            borderRadius: 12,
            background: 'white',
            boxShadow: '0 5px 20px rgba(0,0,0,.1)',
          }}
        >
          <div style={{ fontSize: 60 }}>⏳</div>

          <h1
            style={{
              fontSize: '28px',
              marginBottom: '10px',
              color: '#333',
              fontWeight: 'bold',
            }}
          >
            Opening {viewerName}
          </h1>

          {loading ? (
            <p>{shouldAddTicket ? 'Generating secure access...' : 'Preparing redirect...'}</p>
          ) : (
            <>
              <p
                style={{
                  fontSize: '16px',
                  color: '#666',
                  marginBottom: '30px',
                  lineHeight: '1.6',
                }}
              >
                The page will load in <b>{secondsLeft}</b> second
                {secondsLeft !== 1 ? 's' : ''}
              </p>

              <div
                style={{
                  width: '100%',
                  height: 8,
                  background: '#ddd',
                  borderRadius: 4,
                  overflow: 'hidden',
                  marginTop: 20,
                }}
              >
                <div
                  style={{
                    width: `${((TOTAL_SECONDS - secondsLeft) / TOTAL_SECONDS) * 100}%`,
                    height: '100%',
                    background: '#7c3aed',
                    transition: '0.3s',
                  }}
                />
              </div>

              <p style={{ marginTop: 20 }}>
                If the page does not load automatically,
                <br />
                {urlFinal ? (
                  <a
                    href={urlFinal}
                    style={{ color: '#7c3aed' }}
                  >
                    click here
                  </a>
                ) : (
                  <span>no redirect URL available</span>
                )}
              </p>
            </>
          )}

          <button
            type="button"
            onClick={handleClose}
            style={{
              marginTop: '30px',
              padding: '10px 20px',
              backgroundColor: '#e5e7eb',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#374151',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            ✕ Close tab
          </button>

          {attemptedClose && (
            <p
              style={{
                marginTop: 15,
                fontSize: 12,
                color: '#999',
              }}
            >
              If this tab does not close automatically, close it manually.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
